<template>
  <main>
    <v-toolbar style="background-color: #444; color: #fff; border-radius: 5px">
      <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
    </v-toolbar>
    <v-form @submit.prevent="handleSubmit">
      <formWrapper :validator="$v.form">
        <v-container>
          <v-row>
            <v-col cols="12">
              <locale-select
                v-if="isEdit"
                v-model="locale"
                @change="changeLocale"
              />
            </v-col>

            <v-col cols="12">
              <first-step
                :isEdit="isEdit"
                :form="form"
                :tourismCities="tourism_cities"
                :airports="airports"
                :car_classifications="car_classifications"
              />
            </v-col>

            <v-col cols="12">
              <v-btn
                :loading="loadingButton"
                type="submit"
                class="primary"
                large
                block
                rounded
                >{{ $t("button.save") }}
                <v-icon>mdi-content-save</v-icon></v-btn
              >
            </v-col>
            <!-- :disabled="$v.form.$invalid" -->
          </v-row>
        </v-container>
      </formWrapper>
    </v-form>
  </main>
</template>

<script>
import {
  required,
  maxLength,
  minValue,
  maxValue
} from "vuelidate/lib/validators";
import {
  StoreData,
  ShowData,
  UpdateData,
  IndexData
} from "@/helpers/apiMethods";

export default {
  name: "CreateAndEdit",
  components: {
    FirstStep: () => import("./first_step")
  },
  provide() {
    return {
      $v: this.$v
    };
  },
  data() {
    return {
      loadingButton: false,
      locale: this.locale,
      tourism_cities: [],
      car_classifications: [],
      airports: [],
      form: {
        airport: "",
        tourism_city: "",
        car_classification: "",
        cancel_before_start_in_hours: "",
        reserve_before_start_in_hours: "",
        price: "",
        return_pct: 0,
        hint: "",
        auto_accept_order: 1,
        visible: 1,
        area: ""
      }
    };
  },
  computed: {
    formTitle() {
      if (this.$route.name === "airport_routes_create") {
        return this.$t("heading.create");
      } else {
        return this.$t("heading.edit");
      }
    },
    isEdit() {
      if (this.$route.name === "airport_routes_create") {
        return false;
      } else {
        return true;
      }
    }
  },
  created() {
    this.getTourismCities();
    this.getCarClassifications();
    this.getAirports();
  },

  methods: {
    resetData() {
      this.form.price = "";
      this.form.cancel_before_start_in_hours = "";
      this.form.reserve_before_start_in_hours = "";

      this.$v.form.$reset();
    },
    async getTourismCities() {
      const res = await IndexData({
        reqName: "tourism-cities",
        query: { pagination: "all" }
      });
      const { tourism_cities } = res.data;
      this.tourism_cities = tourism_cities;
    },
    async getAirports() {
      const res = await IndexData({
        reqName: "airports",
        query: { pagination: "all" }
      });
      const { airports } = res.data;
      this.airports = airports;
    },

    async getCarClassifications() {
      const res = await IndexData({
        reqName: "car-classifications",
        query: { pagination: "all" }
      });
      const { car_classifications } = res.data;
      this.car_classifications = car_classifications;
    },

    handleShowData(locale = this.locale) {
      ShowData({
        reqName: "airport-car-routes",
        param: this.$route.params.slug,
        locale
      }).then(res => {
        const { airport_car_route } = res.data;
        for (const key in this.form) {
          this.form[key] = airport_car_route[key];
        }
        this.form.tourism_city = airport_car_route["city"]["id"];
        this.form.car_classification =
          airport_car_route["car_classification"]["id"];
        this.form.airport = airport_car_route["airport"]["id"];
        this.form.visible = airport_car_route.visible ? 1 : 0;
        this.form.auto_accept_order = airport_car_route.auto_accept_order
          ? 1
          : 0;
      });
    },
    generateData() {
      const formData = new FormData();

      for (const key in this.form) {
        formData.append(key, this.form[key] ? this.form[key] : "");
      }
      formData.append(
        "return_pct",
        this.form["return_pct"] ? this.form["return_pct"] : 0
      );

      if (this.isEdit) {
        formData.append("_method", "put");
        formData.append("locale", this.locale);
      }
      return formData;
    },
    onCreate() {
      StoreData({ reqName: "airport-car-routes", data: this.generateData() })
        .then(() => {
          this.resetData();
        })
        .finally(() => {
          this.loadingButton = false;
        });
    },
    onUpdate() {
      UpdateData({
        reqName: "airport-car-routes",
        data: this.generateData(),
        param: this.$route.params.slug,
        locale: this.locale
      })
        .then(() => {
          this.$router.push("/car/airport-car-routes");
        })
        .finally(() => {
          this.loadingButton = false;
        });
    },
    handleSubmit() {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid) {
        this.loadingButton = true;

        if (!this.isEdit) {
          // in case create
          this.onCreate();
        } else {
          // in case edit
          this.onUpdate();
        }
      }
    },
    changeLocale(locale) {
      this.locale = locale;
      this.handleShowData(locale);
    }
  },
  validations() {
    return {
      form: {
        airport: {
          required
        },
        tourism_city: {
          required
        },

        car_classification: {
          required
        },
        cancel_before_start_in_hours: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(336)
        },
        reserve_before_start_in_hours: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(336)
        },

        price: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(50000)
        },
        return_pct: {
          minValue: minValue(0),
          maxValue: maxValue(100)
        },
        area: {
          maxLength: maxLength(190)
        },
        hint: {
          maxLength: maxLength(190)
        }
      }
    };
  },

  watch: {
    isEdit: {
      handler(value) {
        if (value) {
          this.handleShowData();
        }
      },
      immediate: true
    },
    $route: {
      handler({ query }) {
        if (query["tourism_city"]) {
          this.form.tourism_city = Number(query["tourism_city"]);
        }
        if (query["car_classification"]) {
          this.form.car_classification = Number(query["car_classification"]);
        }
      },
      immediate: true
    }
  }
};
</script>
